import { useEffect, useState } from 'react';

import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Box, FormControl, TextField } from '@material-ui/core';

import { PickerSelect } from '../PickerSelect';

export const SystemPickerForGroup = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    formContext,
    name,
    schema: { title = 'System', description = '' } = {},
    required = false,
  } = props;

  const selectedSystem = (formContext?.formData?.[name] as string) || '';
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);

  // Extract product_area from form context
  const productAreaRef = formContext?.formData?.product_area as string;

  const [availableSystems, setAvailableSystems] = useState<Entity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Fetch systems filtered by the selected group
  useEffect(() => {
    const fetchFilteredSystems = async () => {
      if (!productAreaRef) {
        setAvailableSystems([]);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // Parse the selected group to extract the group name
        const groupRef = parseEntityRef(productAreaRef);
        const groupName = groupRef.name; // e.g., "carrier-digital-dpx-pe"

        // Fetch all System entities
        const { items: allSystems } = await catalogApi.getEntities({
          filter: {
            kind: 'System',
          },
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.title',
            'metadata.description',
            'spec.owner',
            'spec.type',
            'spec.lifecycle',
          ],
        });

        // Filter systems based on ownership
        const filteredSystems = allSystems.filter(system => {
          if (!system.spec?.owner) {
            return false;
          }

          const owner = system.spec.owner as string;

          if (owner.startsWith('group:')) {
            try {
              const ownerRef = parseEntityRef(owner);
              const ownerName = ownerRef.name;
              return (
                ownerName === groupName || ownerName.startsWith(`${groupName}-`)
              );
            } catch {
              return false;
            }
          } else {
            return owner === groupName || owner.startsWith(`${groupName}-`);
          }
        });

        setAvailableSystems(filteredSystems);
      } catch (err) {
        errorApi.post(new Error(`Error fetching systems for group: ${err}`));
        setError('Failed to load systems');
        setAvailableSystems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredSystems();
  }, [productAreaRef, catalogApi, errorApi]);

  // Auto-select system if there's only one available
  useEffect(() => {
    if (
      availableSystems.length === 1 &&
      !selectedSystem &&
      !loading &&
      !error &&
      productAreaRef
    ) {
      const autoSelectedSystem = stringifyEntityRef(availableSystems[0]);
      onChange(autoSelectedSystem);
    }
  }, [
    availableSystems,
    selectedSystem,
    loading,
    error,
    productAreaRef,
    onChange,
  ]);

  const allErrors = [...(rawErrors || [])];
  const errorText = allErrors?.[0] as string | undefined;

  if (!productAreaRef) {
    return (
      <Box>
        <FormControl fullWidth margin="normal" error={Boolean(errorText)}>
          <TextField
            label={title}
            value=""
            disabled
            placeholder="Select a Product Area first..."
            helperText="Please select a Product Area above to see available systems"
            variant="outlined"
            required={required}
          />
        </FormControl>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <FormControl fullWidth margin="normal">
          <TextField
            label={title}
            value=""
            disabled
            placeholder="Loading systems..."
            helperText="Loading systems for the selected Product Area..."
            variant="outlined"
            required={required}
          />
        </FormControl>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <FormControl fullWidth margin="normal" error>
          <TextField
            label={title}
            value=""
            disabled
            placeholder="Error loading systems"
            helperText={error}
            variant="outlined"
            required={required}
            error
          />
        </FormControl>
      </Box>
    );
  }

  if (availableSystems.length === 0) {
    return (
      <Box>
        <FormControl fullWidth margin="normal">
          <TextField
            label={title}
            value=""
            disabled
            placeholder="No systems available"
            helperText="No systems found for the selected Product Area"
            variant="outlined"
            required={required}
          />
        </FormControl>
      </Box>
    );
  }

  const options = availableSystems.map(system => ({
    value: stringifyEntityRef(system),
    label: system.metadata.title || system.metadata.name,
    description: system.metadata.description,
  }));

  return (
    <PickerSelect
      title={title}
      description={
        description || 'Systems available for the selected Product Area'
      }
      required={required}
      value={selectedSystem}
      onChange={onChange}
      options={options}
      labelId="system-picker-label"
      errorText={errorText}
    />
  );
};
